import { useForm as createUseForm } from "@ant-design-vue/use";
import { Button, Modal } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../app.logic";
import { createFieldSelect } from "../../../components/input/template-builder/template-builder.logic";
import i18n from "../../../i18n";
import store from "../../../store";
import { modalStyleForm } from "../../../utils/constant";
import { initUseForm } from "../../../utils/form";
import { createLoading } from "../../../utils/loading";
import { has } from "../../../utils/menu-context";
import { addRules, required, requiredWhen, validateError } from "../../../utils/validate";
import { callDepartmentRandomSamplingDepartment } from "../sampling-monitor/sampling-monitor/sampling-monitor.section";
import { createConditionForm } from "./calendar.condition";
import { callCalendarType, createField } from "./calendar.section";

const CalendarForm = defineAsyncComponent(() => import("./CalendarForm"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/calendar";
export const routerPrefix = "calendars";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const onSearch = ({ page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where, sort } }).finally(() => setIsLoading(false));
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createFormTitle = ({ data }) => {
    const { modelRef } = data.useForm;
    return modelRef.id ? modelRef.name : i18n.global.t("button.create");
};

export const createForm = () => {
    return {
        refCalendarType: null,
        startDatetime: null,
        endDatetime: null,
        isAllDay: null,
        name: null,
        desc: null,
        attribute: null,
        refDepartment: null
    };
};

export const createRules = () => {
    return {
        refCalendarType: [required],
        startDatetime: [required],
        endDatetime: [required],
        name: [required]
    };
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page, routerPrefix }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initByRole({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] }, routerPrefix }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = (/* { data } */) => {
    const promises = [];
    return Promise.all(promises);
};

export const initForm = ({ props, data, refs }) => {
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    loadForm({ props, data, refs });
};

export const initByRole = ({ /* props,  */ data, key }) => {
    //const { menuContext, viewOnly } = props;
    const { useForm } = data;
    const { modelRef /* , initialModel */ } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    //const initModel = key ? lodash.get(initialModel, key) : initialModel;
    const prefix = key ? `${key}.` : "";
    addRules({
        useForm,
        modelRef,
        key: `${prefix}refDepartment`,
        rules: [requiredWhen({ when: () => ["Sampling Request"].includes(model.refCalendarType?.key) })]
    });
};

export const openModalForm = ({ props, afterSaveCallback }) => {
    const refs = reactive({ form: null });
    const onOk = () => {
        refs.form.callOnClickModalOk().then(record => {
            setIsLoadingApp(true);
            return callSave({ form: record })
                .finally(() => setIsLoadingApp(false))
                .then(() => {
                    afterSaveCallback?.();
                    options.bind.onCancel();
                });
        });
    };
    const onDelete = () => {
        Modal.confirm({
            title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.delete") }),
            autoFocusButton: "cancel",
            onOk: () => {
                setIsLoadingApp(true);
                return callDelete({ id: props.form.id })
                    .finally(() => setIsLoadingApp(false))
                    .then(() => {
                        afterSaveCallback?.();
                        options.bind.onCancel();
                    });
            },
            okType: "danger"
        });
    };
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleForm), {
            okButtonProps: { disabled: props.viewOnly },
            header: i18n.global.t("model.calendar.modelName"),
            footer: () => {
                let buttonDelete = null;
                if (props?.form.id && has({ menuContext: props.menuContext, key: "canDelete" })) {
                    buttonDelete = (
                        <Button class="btn-danger" onClick={onDelete}>
                            {i18n.global.t("button.delete")}
                        </Button>
                    );
                }
                return (
                    <div class="d-flex">
                        {buttonDelete}
                        <Button class="ml-auto" onClick={() => options.bind.onCancel()}>
                            {i18n.global.t("button.cancel")}
                        </Button>
                        <Button class="btn-primary" onClick={onOk}>
                            {i18n.global.t("button.ok")}
                        </Button>
                    </div>
                );
            }
        }),
        render: () => (
            <CalendarForm
                ref={e => {
                    if (e) refs.form = e;
                }}
                {...props}
            ></CalendarForm>
        )
    });
    return options;
};

export const state = reactive({
    useForm: createUseForm(
        {
            refCalendarType: null,
            refDepartment: null
        },
        {
            refCalendarType: [required],
            refDepartment: [required]
        }
    ),
    fields: [
        createFieldSelect({
            key: "refCalendarType",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.calendar.refCalendarType"),
            inputProp: () => ({
                value: state.useForm.modelRef.refCalendarType,
                "onUpdate:value": value => (state.useForm.modelRef.refCalendarType = value),
                list: callCalendarType,
                optLabel: "value"
            })
        }),
        createFieldSelect({
            key: "refDepartment",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.calendar.refDepartment"),
            inputProp: () => ({
                value: state.useForm.modelRef.refDepartment,
                "onUpdate:value": value => (state.useForm.modelRef.refDepartment = value),
                list: callDepartmentRandomSamplingDepartment,
                optLabel: "nameTh"
            })
        })
    ]
});
